import { useFlags } from 'launchdarkly-react-client-sdk';
import { isDebugMode } from '../../debug/debugUser';
import { MaggieFeatureFlags } from '../../util/feature-flags';
import { FROST_FEATURE_KEYS, FrostTarget } from './frosted-gate.constants';
import { useSiteFeatureKeys } from './useSiteFeatureKeys';

export interface FeatureEntitlement {
  isEntitled: boolean;
  /** The site's plan is still loading; the feature counts as included meanwhile. */
  isChecking?: boolean;
}

/** A missing or unreadable plan counts as entitled: a slow or failed lookup must never hide a paid module. */
function useSitePlanEntitlement(key: string): FeatureEntitlement {
  const { keys, isLoading } = useSiteFeatureKeys();
  return keys ? { isEntitled: keys.includes(key) } : { isEntitled: true, isChecking: isLoading };
}

/**
 * Debug sessions, which every e2e run is, see every module so the suite never depends on the test
 * tenant's plan. Picked once at module load because debug mode itself only changes on reload.
 */
export const useFeatureEntitlement: (key: string) => FeatureEntitlement = isDebugMode()
  ? () => ({ isEntitled: true })
  : useSitePlanEntitlement;

/**
 * The flag is the rollout switch and the site's plan decides per target. Existing
 * permission and flag gates stay in place until every client carries a plan.
 */
export function isFrosted(flags: Partial<MaggieFeatureFlags>, entitlement: FeatureEntitlement): boolean {
  return Boolean(flags.showFrostedModules) && !entitlement.isEntitled;
}

/** `undefined` while the site's plan is still loading. */
export function useFrostState(target: FrostTarget): boolean | undefined {
  const flags = useFlags<MaggieFeatureFlags>();
  const entitlement = useFeatureEntitlement(FROST_FEATURE_KEYS[target]);
  if (flags.showFrostedModules && entitlement.isChecking) {
    return undefined;
  }
  return isFrosted(flags, entitlement);
}

export function useIsFrosted(target: FrostTarget): boolean {
  return useFrostState(target) === true;
}
